/**
 * OTP resend cooldown: exponential backoff stored in sessionStorage
 * so the timer persists across page reloads.
 *
 * - Cooldown = BASE_SECONDS * 2^attemptIndex (capped at MAX_COOLDOWN).
 * - Key: otp_resend_{scope}_{normalizedEmail}
 * - Value: { lastRequestAt: number, attemptIndex: number }
 */

const STORAGE_PREFIX = "otp_resend_";
const BASE_SECONDS = 60;
const MAX_COOLDOWN_SECONDS = 900; // 15 minutes

export type OTPResendScope = "signup" | "login" | "forgot_password";

function getStorageKey(scope: OTPResendScope, email: string): string {
	const normalized = (email || "").toLowerCase().trim();
	return `${STORAGE_PREFIX}${scope}_${normalized}`;
}

function safeSessionStorage(): Storage | null {
	if (typeof window === "undefined") return null;
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

export interface OTPCooldownState {
	lastRequestAt: number;
	attemptIndex: number;
}

export function getCooldownSeconds(attemptIndex: number): number {
	const seconds = BASE_SECONDS * Math.pow(2, attemptIndex);
	return Math.min(Math.max(seconds, BASE_SECONDS), MAX_COOLDOWN_SECONDS);
}

export function getStoredState(
	scope: OTPResendScope,
	email: string
): OTPCooldownState | null {
	const storage = safeSessionStorage();
	if (!storage || !email) return null;
	try {
		const raw = storage.getItem(getStorageKey(scope, email));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as OTPCooldownState;
		if (
			typeof parsed.lastRequestAt !== "number" ||
			typeof parsed.attemptIndex !== "number"
		) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

/**
 * Returns how many seconds remain until the user can resend OTP.
 * 0 if they can send now.
 */
export function getCooldownRemainingSeconds(
	scope: OTPResendScope,
	email: string
): number {
	const state = getStoredState(scope, email);
	if (!state) return 0;
	const cooldownSec = getCooldownSeconds(state.attemptIndex);
	const elapsed = (Date.now() - state.lastRequestAt) / 1000;
	const remaining = Math.ceil(cooldownSec - elapsed);
	return remaining > 0 ? remaining : 0;
}

/**
 * Call after successfully sending/resending OTP.
 * Stores lastRequestAt = now and increments attemptIndex.
 * Returns the cooldown duration (in seconds) that now applies.
 */
export function recordOtpSent(scope: OTPResendScope, email: string): number {
	const storage = safeSessionStorage();
	if (!storage || !email) return BASE_SECONDS;
	const key = getStorageKey(scope, email);
	const state = getStoredState(scope, email);
	const attemptIndex = (state?.attemptIndex ?? 0) + 1;
	const cooldownSec = getCooldownSeconds(attemptIndex - 1);
	storage.setItem(
		key,
		JSON.stringify({
			lastRequestAt: Date.now(),
			attemptIndex,
		})
	);
	return cooldownSec;
}

/**
 * Clear cooldown for this scope+email (e.g. after successful verification).
 */
export function clearOtpCooldown(scope: OTPResendScope, email: string): void {
	const storage = safeSessionStorage();
	if (!storage || !email) return;
	storage.removeItem(getStorageKey(scope, email));
}
